import React, { useEffect, useState } from 'react';
import { cn } from '../../lib/cn';
import type { PfSyncStatus } from '../../patient-forms/types';
import {
  PF_SYNC_LABEL,
  pfAssignmentTone,
  pfLifecycleLabel,
  pfSyncTone,
} from '../../patient-forms/utils';
import { FM_SENT_FILTERS, fmFilterSentForms, fmSentCounts } from '../../forms-queue/utils';
import type { FmSentCounts, FmSentFilter, FmSentFormRow } from '../../forms-queue/types';
import { Button } from '../Button/Button';
import { ElvaPageLoader } from '../ElvaPageLoader/ElvaPageLoader';
import { Icon } from '../Icon/Icon';
import type { IconKind } from '../Icon/Icon';
import { Input } from '../Input/Input';
import { Pagination } from '../Pagination/Pagination';

const PAGE_SIZE = 10;

export interface SentFormsViewProps {
  rows: FmSentFormRow[];
  filter: FmSentFilter;
  onFilterChange: (filter: FmSentFilter) => void;
  search: string;
  onSearchChange: (search: string) => void;
  onOpenPatient?: (row: FmSentFormRow) => void;
  onPreview?: (row: FmSentFormRow) => void;
  /** Resend the form link to the patient, by text AND email. */
  onSendReminder?: (row: FmSentFormRow) => void;
  isLoading?: boolean;
}

const SYNC_ICON: Record<PfSyncStatus, IconKind> = {
  not_started: 'clock',
  synced: 'check',
};

// Fixed pixel widths (not fr) for every column but Form — each row is its own grid,
// so fr tracks size to that row's own content and drift out of alignment between rows.
const COLS =
  'grid min-w-[1000px] grid-cols-[180px_minmax(160px,1fr)_190px_110px_110px_200px] items-center gap-3';

function StatusPill({ tone, children }: { tone: string; children: React.ReactNode }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-[99px] px-2.5 py-0.5 text-[11px] font-semibold"
      style={{
        color: `color-mix(in srgb, ${tone} 88%, #000)`,
        background: `color-mix(in srgb, ${tone} 12%, var(--cream-1))`,
      }}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: tone }} />
      {children}
    </span>
  );
}

const SUMMARY: { key: keyof FmSentCounts; label: string; color: string }[] = [
  { key: 'all', label: 'Sent', color: 'var(--ai-3)' },
  { key: 'needs_review', label: 'Needs review', color: 'var(--gold)' },
  { key: 'with_patient', label: 'With patient', color: 'var(--info)' },
  { key: 'synced', label: 'Synced', color: 'var(--success)' },
];

function SentFormRow({
  row,
  onOpenPatient,
  onPreview,
  onSendReminder,
}: {
  row: FmSentFormRow;
  onOpenPatient?: (row: FmSentFormRow) => void;
  onPreview?: (row: FmSentFormRow) => void;
  onSendReminder?: (row: FmSentFormRow) => void;
}) {
  const [reminded, setReminded] = useState(false);
  const isSynced = row.syncStatus === 'synced';
  const { sentAt, closedAt } = row.timeline;
  const canRemind =
    row.assignmentStatus === 'pending' ||
    row.assignmentStatus === 'in_progress' ||
    row.assignmentStatus === 'submitted';

  return (
    <div className={`${COLS} border-t border-[var(--cream-2)] px-4 py-3 text-[12.5px]`}>
      <button
        type="button"
        onClick={() => {
          onOpenPatient?.(row);
        }}
        className="truncate text-left font-semibold text-[var(--ink-0)] hover:text-[var(--ai-3)] hover:underline"
      >
        {row.patientName}
      </button>

      <div className="min-w-0">
        <div className="truncate font-medium text-[var(--ink-1)]">{row.formName}</div>
        <div className="truncate text-[11.5px] text-[var(--ink-4)]">{row.formDesc}</div>
      </div>

      <div className="flex flex-col items-start gap-1">
        <StatusPill tone={pfAssignmentTone(row.assignmentStatus, row.syncStatus)}>
          {pfLifecycleLabel(row.assignmentStatus, row.syncStatus)}
        </StatusPill>
        {isSynced ? (
          <span
            className="inline-flex items-center gap-1 text-[10.5px] font-semibold"
            style={{ color: pfSyncTone(row.syncStatus) }}
          >
            <Icon kind={SYNC_ICON[row.syncStatus]} size={11} stroke={2.2} />
            {PF_SYNC_LABEL[row.syncStatus]}
          </span>
        ) : null}
      </div>

      <div className="pc-mono-tab text-[11.5px] text-[var(--ink-2)]">
        {sentAt ?? '—'}
        {row.channel != null ? (
          <span className="block text-[10.5px] text-[var(--ink-4)]">{row.channel}</span>
        ) : null}
      </div>

      <div className="pc-mono-tab text-[11.5px] text-[var(--ink-2)]">
        {closedAt != null ? (
          <>
            {closedAt}
            {row.reviewedBy != null ? (
              <span className="block text-[10.5px] text-[var(--ink-4)]">by {row.reviewedBy}</span>
            ) : null}
          </>
        ) : (
          <span className="text-[var(--ink-4)]">—</span>
        )}
      </div>

      <div className="flex items-center justify-end gap-1.5">
        <Button
          variant="ghost"
          size="sm"
          iconLeft="eye"
          onClick={() => {
            onPreview?.(row);
          }}
        >
          Preview
        </Button>
        {canRemind ? (
          <Button
            variant="secondary"
            size="sm"
            iconLeft={reminded ? 'check' : 'send'}
            title="Send the form link to the patient by text and email"
            onClick={() => {
              onSendReminder?.(row);
              setReminded(true);
              window.setTimeout(() => {
                setReminded(false);
              }, 2500);
            }}
          >
            {reminded ? 'Sent' : 'Remind'}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export function SentFormsView({
  rows,
  filter,
  onFilterChange,
  search,
  onSearchChange,
  onOpenPatient,
  onPreview,
  onSendReminder,
  isLoading = false,
}: SentFormsViewProps) {
  const counts = fmSentCounts(rows);
  const visible = fmFilterSentForms(rows, filter, search);

  const [page, setPage] = useState(1);
  // Filtering/searching changes the result set — always return to the first page.
  useEffect(() => {
    setPage(1);
  }, [filter, search]);

  const pageCount = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const current = Math.min(page, pageCount);
  const start = (current - 1) * PAGE_SIZE;
  const pageRows = visible.slice(start, start + PAGE_SIZE);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="mb-4 flex flex-wrap gap-3">
        {SUMMARY.map(({ key, label, color }) => (
          <div
            key={key}
            className="min-w-[110px] rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-4 py-3 shadow-[var(--sh-1)]"
          >
            <div className="pc-eyebrow mb-1 text-[var(--ink-3)]">{label}</div>
            <div
              className="font-[var(--font-display)] text-2xl font-semibold tracking-[-0.03em]"
              style={{ color }}
            >
              {counts[key]}
            </div>
          </div>
        ))}
      </div>

      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-1.5">
          {FM_SENT_FILTERS.map(({ key, label }) => {
            const active = key === filter;
            return (
              <button
                key={key}
                type="button"
                onClick={() => {
                  onFilterChange(key);
                }}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-[var(--r-pill)] border px-3 py-1.5 text-[12px] font-semibold transition',
                  active
                    ? 'border-[var(--ai-3)] bg-[var(--ai-tint)] text-[var(--ai-3)]'
                    : 'border-[var(--cream-2)] bg-[var(--cream-1)] text-[var(--ink-2)] hover:bg-[var(--cream-0)]',
                )}
              >
                {label}
                <span
                  className={cn(
                    'rounded-full px-1.5 text-[10.5px]',
                    active ? 'bg-[var(--ai-3)] text-white' : 'bg-[var(--cream-2)] text-[var(--ink-3)]',
                  )}
                >
                  {counts[key]}
                </span>
              </button>
            );
          })}
        </div>
        <div className="w-[240px] max-w-full">
          <Input
            icon="search"
            placeholder="Search patient or form"
            value={search}
            onChange={(e) => {
              onSearchChange(e.target.value);
            }}
          />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto rounded-[var(--r-lg)] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-1)]">
        <div
          className={`${COLS} sticky top-0 z-[1] border-b border-[var(--cream-2)] bg-[var(--cream-0)] px-4 py-2.5 font-[var(--font-mono)] text-[9.5px] font-bold uppercase tracking-[0.1em] text-[var(--ink-4)]`}
        >
          <span>Patient</span>
          <span>Form</span>
          <span>Status</span>
          <span>Sent</span>
          <span>Closed</span>
          <span className="text-right">Actions</span>
        </div>
        {isLoading ? (
          <ElvaPageLoader />
        ) : visible.length === 0 ? (
          <div className="px-4 py-16 text-center text-[13px] text-[var(--ink-3)]">
            No sent forms match this view.
          </div>
        ) : (
          pageRows.map((row) => (
            <SentFormRow
              key={row.id}
              row={row}
              {...(onOpenPatient ? { onOpenPatient } : {})}
              {...(onPreview ? { onPreview } : {})}
              {...(onSendReminder ? { onSendReminder } : {})}
            />
          ))
        )}
      </div>

      {!isLoading && visible.length > 0 ? (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <span className="text-[12px] text-[var(--ink-3)]">
            Showing {String(start + 1)}–{String(Math.min(start + PAGE_SIZE, visible.length))} of{' '}
            {String(visible.length)}
          </span>
          {pageCount > 1 ? (
            <Pagination page={current} total={pageCount} onChange={setPage} />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
